import { useState, useEffect, useCallback } from "react";
import { Download01, ChevronDown, ChevronRight } from "@untitledui/icons";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/base/buttons/button";
import { DatePicker } from "@/components/ui/date-picker";
import { getAccountsReceivable, downloadReportCsv, type AccountsReceivableData, type ReceivableCustomer } from "@/api/reports";

const formatCurrency = (amount: number) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "EUR" }).format(amount);

const BUCKET_LABELS: Record<string, string> = {
  current: "Current (0-30)",
  "31-60": "31-60 days",
  "61-90": "61-90 days",
  "90+": "90+ days",
};

const BUCKET_COLORS: Record<string, string> = {
  current: "border-l-green-500",
  days30_60: "border-l-yellow-500",
  days60_90: "border-l-orange-500",
  days90Plus: "border-l-red-500",
};

export default function AccountsReceivableTab() {
  const today = new Date().toISOString().split("T")[0];
  const [asOfDate, setAsOfDate] = useState(today);
  const [data, setData] = useState<AccountsReceivableData | null>(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const result = await getAccountsReceivable(asOfDate);
      setData(result);
    } catch {
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [asOfDate]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleExport = () => {
    downloadReportCsv("accounts-receivable", { asOfDate }, `receivables-${asOfDate}.csv`);
  };

  const toggleExpanded = (customerId: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(customerId)) next.delete(customerId);
      else next.add(customerId);
      return next;
    });
  };

  return (
    <div>
      {/* Controls */}
      <div className="flex flex-wrap items-center gap-3 mb-6">
        <DatePicker label="As of" value={asOfDate} onChange={setAsOfDate} />
        <Button size="md" color="secondary" iconLeading={Download01} onClick={handleExport}>
          Export CSV
        </Button>
      </div>

      {loading ? (
        <div className="py-12 text-center text-text-tertiary">Loading report...</div>
      ) : !data ? (
        <div className="py-12 text-center text-text-tertiary">Failed to load report</div>
      ) : (
        <>
          {/* Aging Bucket Cards */}
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 mb-6">
            <AgingCard label="Current (0-30)" amount={data.agingSummary.current} color={BUCKET_COLORS.current} />
            <AgingCard label="31-60 days" amount={data.agingSummary.days30_60} color={BUCKET_COLORS.days30_60} />
            <AgingCard label="61-90 days" amount={data.agingSummary.days60_90} color={BUCKET_COLORS.days60_90} />
            <AgingCard label="90+ days" amount={data.agingSummary.days90Plus} color={BUCKET_COLORS.days90Plus} />
            <AgingCard label="Total Outstanding" amount={data.agingSummary.totalOutstanding} color="border-l-[var(--color-fg-brand-primary)] shadow-[inset_4px_0_8px_-4px_rgba(21,112,239,0.3)]" highlight />
          </div>

          {/* Customer List */}
          {data.customers.length === 0 ? (
            <div className="rounded-xl border border-border-secondary bg-bg-primary p-8 text-center text-text-tertiary">
              No outstanding invoices
            </div>
          ) : (
            <div className="space-y-3">
              {data.customers.map((cust) => (
                <CustomerRow
                  key={cust.customerId}
                  customer={cust}
                  isExpanded={expanded.has(cust.customerId)}
                  onToggle={() => toggleExpanded(cust.customerId)}
                />
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}

function AgingCard({ label, amount, color, highlight }: {
  label: string;
  amount: number;
  color: string;
  highlight?: boolean;
}) {
  return (
    <div className={`rounded-xl border border-border-secondary bg-bg-primary p-4 border-l-4 ${color}`}>
      <p className="text-xs text-text-tertiary mb-1">{label}</p>
      <p className={`text-lg font-semibold ${highlight ? "text-fg-brand-primary" : "text-text-primary"}`}>
        {formatCurrency(amount)}
      </p>
    </div>
  );
}

function CustomerRow({ customer, isExpanded, onToggle }: {
  customer: ReceivableCustomer;
  isExpanded: boolean;
  onToggle: () => void;
}) {
  const navigate = useNavigate();

  return (
    <div className="rounded-xl border border-border-secondary bg-bg-primary overflow-hidden">
      <button
        type="button"
        onClick={onToggle}
        className="w-full flex items-center justify-between px-4 sm:px-6 py-4 hover:bg-bg-secondary_hover transition-colors"
      >
        <div className="flex items-center gap-3">
          {isExpanded ? (
            <ChevronDown className="size-4 text-text-tertiary shrink-0" />
          ) : (
            <ChevronRight className="size-4 text-text-tertiary shrink-0" />
          )}
          <div className="text-left">
            <p className="text-sm font-medium text-text-primary">{customer.customerName}</p>
            <p className="text-xs text-text-tertiary">{customer.invoices.length} invoice{customer.invoices.length !== 1 ? "s" : ""}</p>
          </div>
        </div>
        <p className="text-sm font-semibold text-text-primary">{formatCurrency(customer.totalOutstanding)}</p>
      </button>

      {isExpanded && (
        <div className="border-t border-border-secondary">
          {/* Mobile cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 p-4 xl:hidden">
            {customer.invoices.map((inv) => (
              <div
                key={inv.id}
                className="rounded-lg border border-border-secondary p-3 cursor-pointer hover:bg-bg-secondary_hover transition-colors"
                onClick={() => navigate(`/invoices/${inv.id}`)}
              >
                <div className="flex items-center justify-between mb-1">
                  <span className="text-sm font-medium text-text-primary">#{inv.invoiceNumber}</span>
                  <BucketBadge bucket={inv.agingBucket} />
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-text-tertiary">Due: {inv.dueDate}</span>
                  <span className="font-medium text-text-primary">{formatCurrency(inv.total)}</span>
                </div>
                {inv.daysOverdue > 0 && (
                  <p className="text-xs text-red-600 mt-1">{inv.daysOverdue} days overdue</p>
                )}
              </div>
            ))}
          </div>

          {/* Desktop table */}
          <table className="hidden xl:table w-full text-sm">
            <thead>
              <tr className="bg-bg-secondary">
                <th className="px-6 py-2 text-left font-medium text-text-tertiary">Invoice #</th>
                <th className="px-6 py-2 text-right font-medium text-text-tertiary">Amount</th>
                <th className="px-6 py-2 text-center font-medium text-text-tertiary">Due Date</th>
                <th className="px-6 py-2 text-center font-medium text-text-tertiary">Days Overdue</th>
                <th className="px-6 py-2 text-center font-medium text-text-tertiary">Bucket</th>
              </tr>
            </thead>
            <tbody>
              {customer.invoices.map((inv) => (
                <tr
                  key={inv.id}
                  className="border-t border-border-secondary cursor-pointer hover:bg-bg-secondary_hover transition-colors"
                  onClick={() => navigate(`/invoices/${inv.id}`)}
                >
                  <td className="px-6 py-3 font-medium text-fg-brand-primary">#{inv.invoiceNumber}</td>
                  <td className="px-6 py-3 text-right font-medium text-text-primary">{formatCurrency(inv.total)}</td>
                  <td className="px-6 py-3 text-center text-text-secondary">{inv.dueDate}</td>
                  <td className="px-6 py-3 text-center">
                    {inv.daysOverdue > 0 ? (
                      <span className="text-red-600 font-medium">{inv.daysOverdue}</span>
                    ) : (
                      <span className="text-text-tertiary">0</span>
                    )}
                  </td>
                  <td className="px-6 py-3 text-center"><BucketBadge bucket={inv.agingBucket} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function BucketBadge({ bucket }: { bucket: string }) {
  const colors: Record<string, string> = {
    current: "bg-green-100 text-green-700",
    "31-60": "bg-yellow-100 text-yellow-700",
    "61-90": "bg-orange-100 text-orange-700",
    "90+": "bg-red-100 text-red-700",
  };
  return (
    <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${colors[bucket] ?? "bg-gray-100 text-gray-700"}`}>
      {BUCKET_LABELS[bucket] ?? bucket}
    </span>
  );
}
